import { useQuery } from "@apollo/client";
import { FIND_COMPANY } from "graphql/queries";
import { useAuthorize } from "graphql/user";
import { TourStep } from "providers/DashboardTourProvider";
import { useI18n } from "providers/I18nProvider";
import { OnboardingStepStatus, PermissionEnum } from "types/graphql/graphql";

export const useDashboardTourSteps = (companyId: string) => {
  const { t } = useI18n();
  const steps: TourStep[] = [];

  const { data: company, loading: companyLoading } = useQuery(FIND_COMPANY, {
    variables: { id: companyId },
  });

  const onboardingSteps = company?.company?.onboardingSteps;
  const showGetStartedChecklist = Object.values(onboardingSteps ?? {}).some(
    (step) => step === OnboardingStepStatus.Pending,
  );

  if (!companyLoading) {
    if (showGetStartedChecklist) {
      steps.push({
        title: t(
          "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.first.title",
        ),
        description: t(
          "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.first.description",
        ),
        selector: ".checklist-tour-step",
      });
    } else {
      steps.push({
        title: t(
          "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.actionItems.title",
        ),
        description: t(
          "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.actionItems.description",
        ),
        selector: ".action-items-tour-step",
      });
    }
  }

  steps.push(
    {
      title: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.personalSettings.title",
      ),
      description: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.personalSettings.description",
      ),
      selector: ".personal-settings-tour-step",
    },
    {
      title: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.languagePreferences.title",
      ),
      description: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.languagePreferences.description",
      ),
      selector: ".language-settings-tour-step",
    },
    {
      title: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.search.title",
      ),
      description: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.search.description",
      ),
      selector: ".search-tour-step",
    },
  );

  const canManageEmployees = useAuthorize(
    PermissionEnum.ListEmployee,
    companyId,
  );

  if (canManageEmployees) {
    steps.push({
      title: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.employees.title",
      ),
      description: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.employees.description",
      ),
      selector: ".employees-tour-step",
    });
  }

  const canManageProducts = useAuthorize(PermissionEnum.ViewBenefit, companyId);

  if (canManageProducts) {
    steps.push({
      title: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.benefits.title",
      ),
      description: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.benefits.description",
      ),
      selector: ".products-tour-step",
    });
  }

  const canManagePayments = useAuthorize(
    PermissionEnum.ViewCompanyPayment,
    companyId,
  );

  if (canManagePayments) {
    steps.push({
      title: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.payments.title",
      ),
      description: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.payments.description",
      ),
      selector: ".payments-tour-step",
    });
  }

  const canManageRequests = useAuthorize(
    PermissionEnum.ManageBenefit,
    companyId,
  );

  if (canManageRequests) {
    steps.push({
      title: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.requests.title",
      ),
      description: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.requests.description",
      ),
      selector: ".requests-tour-step",
    });
  }

  const canManageActivity = useAuthorize(
    PermissionEnum.ViewCompanyDashboard,
    companyId,
  );

  if (canManageActivity) {
    steps.push({
      title: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.activity.title",
      ),
      description: t(
        "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.activity.description",
      ),
      selector: ".activity-tour-step",
    });
  }

  steps.push({
    title: t(
      "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.settings.title",
    ),
    description: t(
      "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.steps.settings.description",
    ),
    selector: ".settings-tour-step",
  });

  return {
    steps,
    companyLoading,
    showGetStartedChecklist,
  };
};
